/**
 * NarrationStudioDialog — writer-facing narrator selection + audiobook generation.
 *
 * Accessible from: Loom File menu → "Narration Studio"
 *
 * Tabs:
 *   1. Choose Narrator — persona cards (portrait, bio, voice sample, video)
 *   2. Generate Audiobook — progress bar + per-chapter audio players (paid only)
 *
 * Payment flow:
 *   1. Writer picks narrator → saved to project
 *   2. "Enable Narration" → Stripe checkout → Stripe redirects back with
 *      `?payment=success&narration_project={pid}` → usePaymentReturn handles it
 *   3. NarrationStudioDialog reopens, detects paid status, shows Generate tab
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mic, Play, Square, Loader2, Check, User, Video, ChevronRight,
  Headphones, Sparkles, X, BookOpen, Zap, Download, RefreshCw,
  Volume2, AlertTriangle, HardDrive, Trash2, CloudDownload, WifiOff,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { usePW } from '@/lib/store';
import { cn } from '@/lib/utils';
import { paymentsApi } from '@/lib/auth_api';
import { streamAndCacheChapter, getCachedChapterIds, getAllCachedChapters, getStorageStats, deleteChapterAudio, clearAllProjectChapters } from '@/utils/NarrationDB';
import { NarrationDownloadDialog } from './NarrationDownloadDialog';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

// ─── Video player (iframe for YouTube, <video> for direct MP4) ─────────────
function VideoPlayer({ url, className }) {
  if (!url) return null;
  const isYouTube = /youtu\.?be/.test(url);
  const embedUrl  = isYouTube
    ? url.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/')
    : null;
  if (embedUrl) {
    return (
      <iframe
        src={embedUrl}
        title="Narrator video"
        className={cn('rounded-lg', className)}
        allow="autoplay; encrypted-media"
        allowFullScreen
      />
    );
  }
  return <video src={url} controls className={cn('rounded-lg', className)} />;
}

// ─── Actor persona card ──────────────────────────────────────────────────────
function ActorCard({ actor, selected, onSelect }) {
  const audioRef       = useRef(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [playing,        setPlaying]        = useState(false);
  const [audioUrl,       setAudioUrl]       = useState(null);
  const [showVideo,      setShowVideo]      = useState(false);

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlaying(false);
  };
  useEffect(() => () => stop(), []);

  const handlePreview = async () => {
    if (playing) { stop(); return; }
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.onended = () => { setPlaying(false); audioRef.current = null; };
      audioRef.current = audio;
      audio.play().then(() => setPlaying(true)).catch(() => toast.error('Playback failed.'));
      return;
    }
    setPreviewLoading(true);
    try {
      const r   = await http.post(`/admin/narration/actors/${actor.id}/sample-audio`);
      const url = r.data.audio_data_url;
      setAudioUrl(url);
      const audio = new Audio(url);
      audio.onended = () => { setPlaying(false); audioRef.current = null; };
      audioRef.current = audio;
      await audio.play();
      setPlaying(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate sample.');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div
      className={cn(
        'relative rounded-xl border-2 transition-all cursor-pointer overflow-hidden',
        selected
          ? 'border-primary bg-primary/5 shadow-md'
          : 'border-border bg-card hover:border-primary/40 hover:shadow-sm',
      )}
      onClick={() => onSelect(actor)}
      data-testid={`narrator-card-${actor.id}`}
    >
      {selected && (
        <div className="absolute top-2.5 right-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
          <Check className="h-3 w-3 text-primary-foreground" />
        </div>
      )}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          {actor.portrait_url ? (
            <img
              src={actor.portrait_url}
              alt={actor.name}
              className="h-14 w-14 rounded-full object-cover border-2 border-border flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 border-2 border-border flex-shrink-0">
              <User className="h-6 w-6 text-primary/60" />
            </div>
          )}
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold truncate">{actor.name}</h3>
            {actor.voice_name && (
              <p className="text-[12px] text-muted-foreground flex items-center gap-1 mt-0.5">
                <Mic className="h-3 w-3" /> {actor.voice_name}
              </p>
            )}
            {actor.narration_tier && (
              <span className={cn(
                'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10.5px] font-medium mt-0.5',
                actor.narration_tier === 'studio'
                  ? 'bg-amber-500/15 text-amber-700'
                  : 'bg-sky-500/15 text-sky-700',
              )}>
                {actor.narration_tier === 'studio' ? <Zap className="h-2.5 w-2.5" /> : <Mic className="h-2.5 w-2.5" />}
                {actor.narration_tier === 'studio' ? 'Studio' : 'Standard'}
              </span>
            )}
          </div>
        </div>

        {actor.bio && (
          <p className="text-[12.5px] text-muted-foreground leading-relaxed line-clamp-3">{actor.bio}</p>
        )}

        <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm" variant="outline"
            onClick={handlePreview}
            disabled={previewLoading}
            className="gap-1 h-7 text-[12px]"
            data-testid={`narrator-sample-btn-${actor.id}`}
          >
            {previewLoading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : playing
                ? <Square className="h-3 w-3 text-primary" />
                : <Headphones className="h-3 w-3" />}
            {previewLoading ? 'Loading…' : playing ? 'Stop' : 'Voice sample'}
          </Button>
          {actor.video_url && (
            <Button
              size="sm" variant="outline"
              onClick={() => setShowVideo((p) => !p)}
              className="gap-1 h-7 text-[12px]"
              data-testid={`narrator-video-btn-${actor.id}`}
            >
              <Video className="h-3 w-3" />
              {showVideo ? 'Hide video' : 'Watch reel'}
            </Button>
          )}
        </div>

        {showVideo && actor.video_url && (
          <div onClick={(e) => e.stopPropagation()}>
            <VideoPlayer url={actor.video_url} className="w-full aspect-video" />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Per-chapter audio player row ────────────────────────────────────────────
function ChapterRow({ chapter, projectId }) {
  const [loading,    setLoading]    = useState(false);
  const [objectUrl,  setObjectUrl]  = useState(null);
  const [cached,     setCached]     = useState(false);
  const [progress,   setProgress]   = useState(0);   // 0-100
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    if (objectUrl) return;
    setLoading(true);
    setProgress(0);
    try {
      const url = await streamAndCacheChapter(
        projectId,
        chapter.chapter_id,
        API,
        ({ loaded, total }) => {
          if (total > 0) setProgress(Math.round((loaded / total) * 100));
        },
      );
      setObjectUrl(url);
      setCached(true);
    } catch (e) {
      toast.error(`Chapter ${chapter.chapter_number}: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId, chapter.chapter_id, chapter.chapter_number, objectUrl]);

  return (
    <div
      className="flex items-center gap-3 rounded-lg border border-border bg-card/60 px-3 py-2.5"
      data-testid={`chapter-row-${chapter.chapter_id}`}
    >
      <div className="flex-shrink-0 flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
        <span className="text-[11px] font-semibold text-primary">{chapter.chapter_number}</span>
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-medium truncate">
          {chapter.title || `Chapter ${chapter.chapter_number}`}
        </p>
        {chapter.char_count > 0 && (
          <p className="text-[11px] text-muted-foreground">
            {chapter.char_count.toLocaleString()} chars · {chapter.model_id?.replace('eleven_', '') || ''}
            {cached && <span className="ml-1.5 text-emerald-600"><Download className="inline h-2.5 w-2.5" /> cached</span>}
          </p>
        )}
        {loading && progress > 0 && progress < 100 && (
          <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
        {objectUrl ? (
          <audio
            ref={audioRef}
            controls
            src={objectUrl}
            className="h-7 w-40"
            data-testid={`chapter-audio-${chapter.chapter_id}`}
          />
        ) : (
          <Button
            size="sm" variant="outline"
            onClick={load}
            disabled={loading}
            className="gap-1 h-7 text-[12px]"
            data-testid={`chapter-load-btn-${chapter.chapter_id}`}
          >
            {loading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <Volume2 className="h-3 w-3" />}
            {loading ? `${progress}%` : 'Load audio'}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Chapter generation section (shown when narration is paid) ───────────────
function GenerateSection({ pid, narration, onRefresh, onChaptersUpdate }) {
  const [status,   setStatus]   = useState(narration?.generation_status || 'idle');
  const [progress, setProgress] = useState(narration?.generation_progress || null);
  const [chapters, setChapters] = useState([]);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const pollStatus = useCallback(async () => {
    try {
      const r = await http.get(`/projects/${pid}/narration/status`);
      const n = r.data.narration || {};
      const newChapters = r.data.chapters || [];
      setStatus(n.generation_status || 'idle');
      setProgress(n.generation_progress || null);
      setChapters(newChapters);
      onChaptersUpdate?.(newChapters);
    } catch {
      // non-fatal
    }
  }, [pid, onChaptersUpdate]);

  // Load on mount + start polling when generating
  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  useEffect(() => {
    if (status === 'generating') {
      pollRef.current = setInterval(pollStatus, 3000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [status, pollStatus]);

  const handleGenerate = async () => {
    setStarting(true);
    try {
      await http.post(`/projects/${pid}/narration/generate`);
      setStatus('generating');
      toast.success('Audiobook generation started in the background.');
      pollStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start generation.');
    } finally {
      setStarting(false);
    }
  };

  const pct = progress
    ? Math.round((progress.current / Math.max(progress.total, 1)) * 100)
    : 0;

  return (
    <div className="space-y-4">
      {/* Status header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          {status === 'ready' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[12px] text-emerald-700 font-medium">
              <Check className="h-3 w-3" /> Audiobook ready — {chapters.length} chapters
            </span>
          ) : status === 'generating' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-[12px] text-amber-700 font-medium">
              <Loader2 className="h-3 w-3 animate-spin" /> Generating…{progress ? ` (${progress.current}/${progress.total})` : ''}
            </span>
          ) : status === 'error' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2.5 py-1 text-[12px] text-rose-700 font-medium">
              <AlertTriangle className="h-3 w-3" /> Generation error — retry below
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2.5 py-1 text-[12px] text-sky-700 font-medium">
              <BookOpen className="h-3 w-3" /> Narration paid — ready to generate
            </span>
          )}
        </div>

        <div className="flex gap-2">
          {(status === 'idle' || status === 'error' || status === 'ready') && (
            <Button
              size="sm"
              onClick={handleGenerate}
              disabled={starting || status === 'generating'}
              className="gap-1"
              data-testid="narration-generate-btn"
            >
              {starting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Zap className="h-3.5 w-3.5" />}
              {status === 'ready' ? 'Re-generate' : 'Generate audiobook'}
            </Button>
          )}
          <Button
            size="sm" variant="outline"
            onClick={() => { pollStatus(); onRefresh?.(); }}
            className="gap-1"
            data-testid="narration-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      {status === 'generating' && (
        <div className="space-y-1">
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[11.5px] text-muted-foreground text-right">{pct}% complete</p>
        </div>
      )}

      {/* Chapter list */}
      {chapters.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          <p className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            Chapters ({chapters.length})
          </p>
          {chapters.map((ch) => (
            <ChapterRow
              key={ch.chapter_id}
              chapter={ch}
              projectId={pid}
            />
          ))}
        </div>
      )}

      {chapters.length === 0 && status !== 'generating' && status !== 'idle' && (
        <p className="text-[12.5px] text-muted-foreground text-center py-4">
          No chapters generated yet. Click "Generate audiobook" to start.
        </p>
      )}
    </div>
  );
}

// ─── Download Manager Panel ───────────────────────────────────────────────────
const fmt_bytes = (b) => {
  if (b < 1024)          return `${b} B`;
  if (b < 1024 * 1024)   return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// Stage 4 — Queue status with per-item cancel
function QueueStatusPanel({ active, queue, chapters, progress, onCancel }) {
  const activeChap = active ? chapters.find((c) => c.chapter_id === active) : null;
  const pct = progress[active] ?? 0;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 overflow-hidden" data-testid="queue-status-panel">
      {active && (
        <div className="flex items-center gap-2.5 px-3 py-2.5">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-amber-900 truncate">
              Downloading Ch.{activeChap?.chapter_number ?? '?'}
              {activeChap?.title ? ` — ${activeChap.title}` : ''}
            </p>
            <div className="mt-1.5 h-1.5 bg-amber-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          <span className="text-[11.5px] font-medium text-amber-700 flex-shrink-0 tabular-nums">{pct}%</span>
        </div>
      )}
      {queue.length > 0 && (
        <div className={cn('px-3 pb-2.5 space-y-1.5', active && 'border-t border-amber-200 pt-2')}>
          <p className="text-[11px] uppercase tracking-wider text-amber-600 font-bold">
            Queue · {queue.length} remaining
          </p>
          <div className="flex flex-wrap gap-1.5">
            {queue.map((qid) => {
              const ch = chapters.find((c) => c.chapter_id === qid);
              return (
                <span
                  key={qid}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-100 border border-amber-300 px-2 py-0.5 text-[11.5px] text-amber-800"
                >
                  Ch.{ch?.chapter_number ?? '?'}
                  <button
                    type="button"
                    title="Remove from queue"
                    onClick={() => onCancel(qid)}
                    className="rounded hover:bg-amber-200 transition-colors"
                    data-testid={`queue-cancel-${qid}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Stage 4 — Compact chapter status grid
function ChapterStatusGrid({ chapters, cacheMap, queue, active, progress, previewChapter, onQueue, onRemove, onPreview }) {
  return (
    <div className="space-y-2.5">
      {/* Legend */}
      <div className="flex items-center justify-between">
        <p className="text-[11.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          Chapters · {chapters.length}
        </p>
        <div className="flex items-center gap-3 text-[10.5px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-emerald-400 inline-block" /> Saved
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-400 inline-block" /> Queued
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-muted-foreground/30 inline-block" /> Not saved
          </span>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-64 overflow-y-auto pr-1">
        {chapters.map((ch) => {
          const isActive     = active === ch.chapter_id;
          const isQueued     = queue.includes(ch.chapter_id);
          const cached       = !!cacheMap[ch.chapter_id];
          const isPreviewing = previewChapter?.chapter_id === ch.chapter_id;
          const pct          = progress[ch.chapter_id] ?? 0;

          const tileBase = 'relative flex flex-col items-center justify-center rounded-xl border p-2.5 transition-all duration-150 select-none';
          let tileColor;
          if (isActive) {
            tileColor = 'bg-primary/10 border-primary/50 cursor-default';
          } else if (isQueued) {
            tileColor = 'bg-amber-50 border-amber-300 cursor-default';
          } else if (cached) {
            tileColor = isPreviewing
              ? 'bg-emerald-100 border-emerald-500 ring-2 ring-emerald-400/40 cursor-pointer'
              : 'bg-emerald-50/70 border-emerald-300 hover:bg-emerald-100 hover:border-emerald-500 cursor-pointer';
          } else {
            tileColor = 'bg-muted/40 border-border hover:border-primary/50 hover:bg-primary/5 cursor-pointer';
          }

          return (
            <button
              key={ch.chapter_id}
              type="button"
              onClick={() => {
                if (cached) {
                  onPreview(ch);
                } else if (!isQueued && !isActive) {
                  onQueue(ch.chapter_id);
                }
              }}
              title={
                cached
                  ? isPreviewing ? 'Click to stop preview' : `Play Ch. ${ch.chapter_number} inline`
                  : isQueued
                    ? `Ch. ${ch.chapter_number} in queue`
                    : isActive
                      ? `Downloading Ch. ${ch.chapter_number}…`
                      : `Save Ch. ${ch.chapter_number} for offline`
              }
              className={cn(tileBase, tileColor)}
              data-testid={`chapter-tile-${ch.chapter_id}`}
            >
              {/* Chapter number */}
              <span className="text-[13px] font-bold text-foreground/80 leading-none">
                {ch.chapter_number}
              </span>

              {/* Status icon row */}
              <span className="mt-1 flex h-3.5 items-center justify-center">
                {isActive ? (
                  <Loader2 className="h-3 w-3 animate-spin text-primary" />
                ) : isQueued ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
                ) : cached ? (
                  isPreviewing
                    ? <Volume2 className="h-3 w-3 text-emerald-600" />
                    : <Check className="h-3 w-3 text-emerald-500" />
                ) : (
                  <CloudDownload className="h-3 w-3 text-muted-foreground/40" />
                )}
              </span>

              {/* Download progress bar along the bottom */}
              {isActive && pct > 0 && pct < 100 && (
                <div
                  className="absolute bottom-0 left-0 h-0.5 bg-primary rounded-b-xl transition-all duration-300"
                  style={{ width: `${pct}%` }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Cached chapter remove hint */}
      {Object.keys(cacheMap).length > 0 && (
        <p className="text-[11px] text-muted-foreground">
          Tap a saved chapter (green) to play it inline. Long-press or use the header "Clear all" to remove.
        </p>
      )}
    </div>
  );
}

// Stage 4 — Inline voice preview bar (sticky bottom)
function InlineAudioPreview({ chapter, url, loading, audioRef, onClose }) {
  return (
    <div
      className="sticky bottom-0 z-10 rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-50/95 to-sky-50/95 backdrop-blur-sm px-4 py-3 shadow-lg shadow-emerald-100/60"
      data-testid="inline-audio-preview"
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
          {loading
            ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
            : <Volume2 className="h-4 w-4 text-emerald-600" />}
        </div>

        {/* Info + player */}
        <div className="flex-1 min-w-0">
          <p className="text-[12.5px] font-semibold text-foreground truncate leading-tight">
            Ch. {chapter.chapter_number}
            {chapter.title ? ` — ${chapter.title}` : ''}
          </p>
          {loading ? (
            <p className="text-[11.5px] text-muted-foreground mt-0.5">Loading audio…</p>
          ) : url ? (
            <audio
              ref={audioRef}
              key={url}
              src={url}
              controls
              autoPlay
              className="mt-1.5 h-7 w-full"
              data-testid="inline-preview-audio"
            />
          ) : null}
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
          data-testid="inline-preview-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function DownloadManagerPanel({ pid, chapters, isPaid, onQueueChange, onOpenOfflineGuide, onGoToNarrator }) {
  const [cacheMap,    setCacheMap]    = useState({});
  const [stats,       setStats]       = useState({ count: 0, totalBytes: 0 });
  const [queue,       setQueue]       = useState([]);
  const [active,      setActive]      = useState(null);
  const [progress,    setProgress]    = useState({});
  const [clearing,    setClearing]    = useState(false);
  const [previewChapter, setPreviewChapter] = useState(null);
  const [previewUrl,     setPreviewUrl]     = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const audioPreviewRef = useRef(null);

  useEffect(() => {
    onQueueChange?.(queue.length + (active ? 1 : 0));
  }, [queue, active, onQueueChange]);

  const refreshCache = useCallback(async () => {
    if (!pid) return;
    try {
      const [rows, s] = await Promise.all([
        getAllCachedChapters(pid),
        getStorageStats(pid),
      ]);
      const m = {};
      rows.forEach((r) => { m[r.chapter_id] = r; });
      setCacheMap(m);
      setStats(s);
    } catch { /* non-fatal */ }
  }, [pid]);

  useEffect(() => { refreshCache(); }, [refreshCache, chapters]);

  // Process download queue — one at a time
  const isProcessing = useRef(false);
  useEffect(() => {
    if (isProcessing.current || queue.length === 0) return;
    const [next, ...rest] = queue;
    if (cacheMap[next]) { setQueue(rest); return; }
    isProcessing.current = true;
    setActive(next);
    setQueue(rest);
    streamAndCacheChapter(pid, next, API, ({ loaded, total }) => {
      if (total > 0) setProgress((p) => ({ ...p, [next]: Math.round((loaded / total) * 100) }));
    })
      .then(() => {
        setProgress((p) => ({ ...p, [next]: 100 }));
        refreshCache();
      })
      .catch((e) => toast.error(`Download failed: ${e.message}`))
      .finally(() => { isProcessing.current = false; setActive(null); });
  }, [queue, cacheMap, pid, refreshCache]);

  const enqueue = (chapterId) => {
    if (queue.includes(chapterId) || active === chapterId || cacheMap[chapterId]) return;
    setQueue((q) => [...q, chapterId]);
  };

  const enqueueAll = () => {
    const missing = chapters
      .map((c) => c.chapter_id)
      .filter((id) => !cacheMap[id] && !queue.includes(id) && active !== id);
    setQueue((q) => [...q, ...missing]);
  };

  const removeFromCache = async (chapterId) => {
    try {
      await deleteChapterAudio(pid, chapterId);
      await refreshCache();
      if (previewChapter?.chapter_id === chapterId) {
        audioPreviewRef.current?.pause();
        setPreviewChapter(null);
        setPreviewUrl(null);
      }
      toast.success('Chapter removed from offline storage.');
    } catch { toast.error('Could not remove chapter.'); }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const n = await clearAllProjectChapters(pid);
      await refreshCache();
      audioPreviewRef.current?.pause();
      setPreviewChapter(null);
      setPreviewUrl(null);
      toast.success(`Cleared ${n} chapter${n !== 1 ? 's' : ''} from offline storage.`);
    } catch { toast.error('Could not clear storage.'); }
    finally { setClearing(false); }
  };

  const cancelFromQueue = (chapterId) => setQueue((q) => q.filter((id) => id !== chapterId));

  const handlePreviewChapter = async (ch) => {
    if (previewChapter?.chapter_id === ch.chapter_id) {
      audioPreviewRef.current?.pause();
      setPreviewChapter(null);
      setPreviewUrl(null);
      return;
    }
    audioPreviewRef.current?.pause();
    setPreviewChapter(ch);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const url = await streamAndCacheChapter(pid, ch.chapter_id, API);
      setPreviewUrl(url);
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
      setPreviewChapter(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => () => { audioPreviewRef.current?.pause(); }, []);

  const cachedCount = Object.keys(cacheMap).length;
  const totalChaps  = chapters.length;
  const allCached   = totalChaps > 0 && cachedCount >= totalChaps;

  return (
    <div className="space-y-4 flex flex-col" data-testid="download-manager-panel">

      {/* Header / stats bar */}
      <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-3">
          <HardDrive className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-[13px] font-medium">
              {cachedCount === 0
                ? 'No chapters saved offline'
                : `${cachedCount} of ${totalChaps} chapter${totalChaps !== 1 ? 's' : ''} saved offline`}
            </p>
            {stats.totalBytes > 0 && (
              <p className="text-[11.5px] text-muted-foreground">
                {fmt_bytes(stats.totalBytes)} stored on this device
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!allCached && totalChaps > 0 && (
            <Button
              size="sm"
              onClick={enqueueAll}
              disabled={queue.length > 0 || active !== null}
              className="gap-1 h-7 text-[12px]"
              data-testid="download-all-btn"
            >
              <CloudDownload className="h-3.5 w-3.5" />
              {queue.length > 0 || active ? 'Downloading…' : 'Download all'}
            </Button>
          )}
          {stats.count > 0 && (
            <Button
              size="sm" variant="ghost"
              onClick={clearAll}
              disabled={clearing}
              className="gap-1 h-7 text-[12px] text-muted-foreground hover:text-destructive"
              data-testid="download-clear-all-btn"
            >
              {clearing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              Clear all
            </Button>
          )}
        </div>
      </div>

      {/* iOS / Android offline note */}
      <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2.5 text-[11.5px] text-sky-700">
        <WifiOff className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>
          Saved chapters play offline even without Wi-Fi.{' '}
          <strong>On iOS and Android</strong> — install the app to your Home Screen for persistent storage that survives browser restarts.
        </span>
        <button
          type="button"
          onClick={onOpenOfflineGuide}
          className="ml-auto whitespace-nowrap rounded-md bg-sky-100 px-2 py-0.5 text-[11.5px] font-medium text-sky-800 hover:bg-sky-200 transition-colors"
          data-testid="narr-offline-setup-btn"
        >
          Setup Guide →
        </button>
      </div>

      {/* Queue status panel (enhanced with per-item cancel) */}
      {isPaid && (active || queue.length > 0) && (
        <QueueStatusPanel
          active={active}
          queue={queue}
          chapters={chapters}
          progress={progress}
          onCancel={cancelFromQueue}
        />
      )}

      {/* Upsell banner for unpaid narration */}
      {!isPaid && (
        <div className="rounded-xl border border-primary/25 bg-primary/5 px-4 py-4 text-center space-y-2">
          <Headphones className="mx-auto h-6 w-6 text-primary/60" />
          <p className="text-[13px] font-semibold text-foreground">Enable narration to unlock downloads</p>
          <p className="text-[12px] text-muted-foreground">
            Once narration is enabled and your audiobook is generated, you can save
            chapters here for offline playback on any device.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={onGoToNarrator}
            data-testid="downloads-upsell-narrator-cta"
          >
            <Mic className="h-3.5 w-3.5" /> Go to Choose Narrator
          </Button>
        </div>
      )}

      {/* Chapter status grid */}
      {isPaid && (chapters.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-8 text-center">
          <Download className="mx-auto mb-2 h-5 w-5 text-muted-foreground/40" />
          <p className="text-[13px] text-muted-foreground">
            Generate the audiobook first to enable offline downloads.
          </p>
        </div>
      ) : (
        <ChapterStatusGrid
          chapters={chapters}
          cacheMap={cacheMap}
          queue={queue}
          active={active}
          progress={progress}
          previewChapter={previewChapter}
          onQueue={enqueue}
          onRemove={removeFromCache}
          onPreview={handlePreviewChapter}
        />
      ))}

      {/* Inline voice preview (Stage 4) */}
      {previewChapter && (
        <InlineAudioPreview
          chapter={previewChapter}
          url={previewUrl}
          loading={previewLoading}
          audioRef={audioPreviewRef}
          onClose={() => {
            audioPreviewRef.current?.pause();
            setPreviewChapter(null);
            setPreviewUrl(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────────────
export default function NarrationStudioDialog({ open, onOpenChange }) {
  const { project } = usePW();
  const pid = project?.id;

  const [tab,            setTab]            = useState('narrator');
  const [actors,         setActors]         = useState([]);
  const [narration,      setNarration]      = useState(null);
  const [pricePerBook,   setPricePerBook]   = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [selectedActorId, setSelectedActorId] = useState(null);
  const [saving,         setSaving]         = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [chapters,       setChapters]       = useState([]);   // populated once Generate tab loads
  const [queueCount,     setQueueCount]     = useState(0);    // active+queued download count for badge
  const [offlineGuideOpen, setOfflineGuideOpen] = useState(false);  // Stage 4 — offline setup dialog
  const previewAudioRef = useRef(null);

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const [actorsRes, narrRes] = await Promise.all([
        http.get('/narration/actors'),
        http.get(`/projects/${pid}/narration`),
      ]);
      setActors(actorsRes.data.actors || []);
      setNarration(narrRes.data.narration);
      setPricePerBook(narrRes.data.price_per_book_cents);
      setSelectedActorId(narrRes.data.narration?.actor_id || null);
    } catch {
      toast.error('Could not load narration data.');
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => {
    if (open) {
      load();
      setPreviewAudioUrl(null);
    }
  }, [open, pid, load]);

  // Stop preview audio when dialog closes
  useEffect(() => {
    if (!open && previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
  }, [open]);

  const selectedActor = actors.find((a) => a.id === selectedActorId) || null;
  const isPaid = narration?.status === 'paid' || narration?.status === 'ready';

  // Auto-switch to Generate tab when narration is newly paid
  useEffect(() => {
    if (isPaid && tab === 'narrator' && narration?.generation_status === 'idle') {
      // Don't auto-switch — let user choose; just ensure the tab is accessible
    }
  }, [isPaid, tab, narration]);

  const handleSelect = async (actor) => {
    setSelectedActorId(actor.id);
    setSaving(true);
    try {
      const r = await http.put(`/projects/${pid}/narration`, {
        actor_id: actor.id,
        voice_id: actor.voice_id,
      });
      setNarration(r.data.narration);
      setPricePerBook(r.data.price_per_book_cents ?? pricePerBook);
      toast.success(`Narrator set to ${actor.name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save narrator choice.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedActorId) {
      toast.info('Select a narrator first to hear a preview from your manuscript.');
      return;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setPreviewAudioUrl(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const r   = await http.post(`/projects/${pid}/narration/preview`, {}, { timeout: 90000 });
      const url = r.data.audio_data_url;
      setPreviewAudioUrl(url);
      const audio = new Audio(url);
      audio.onended = () => { previewAudioRef.current = null; };
      previewAudioRef.current = audio;
      await audio.play();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview generation failed.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleEnableNarration = async () => {
    if (!selectedActorId) {
      toast.info('Select a narrator first.');
      return;
    }
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const result = await paymentsApi.checkoutNarration(pid, origin);
      // Redirect to Stripe hosted checkout
      window.location.href = result.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout. Check Stripe settings.');
      setCheckoutLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] w-[min(95vw,58rem)] max-w-[58rem] flex-col overflow-hidden"
        data-testid="narration-studio-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-primary" />
            Narration Studio
          </DialogTitle>
          <DialogDescription>
            Choose a narrator, preview your manuscript, and generate a full audiobook.
          </DialogDescription>
        </DialogHeader>

        {/* Tab bar */}
        <div className="flex gap-1 rounded-lg bg-muted/40 p-1 w-fit">
          <button
            type="button"
            onClick={() => setTab('narrator')}
            className={cn(
              'px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
              tab === 'narrator'
                ? 'bg-background border border-border shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid="tab-narrator"
          >
            <Mic className="inline h-3 w-3 mr-1" />Choose Narrator
          </button>
          <button
            type="button"
            onClick={() => isPaid ? setTab('generate') : toast.info('Enable narration first to access chapter generation.')}
            className={cn(
              'px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
              tab === 'generate'
                ? 'bg-background border border-border shadow-sm text-foreground'
                : isPaid
                  ? 'text-muted-foreground hover:text-foreground'
                  : 'text-muted-foreground/50 cursor-not-allowed',
            )}
            data-testid="tab-generate"
          >
            <Zap className="inline h-3 w-3 mr-1" />Generate Audiobook
            {isPaid && (
              <span className="ml-1.5 rounded-full bg-emerald-500/20 px-1 py-0.5 text-[10px] text-emerald-700">Unlocked</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('downloads')}
            className={cn(
              'px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors',
              tab === 'downloads'
                ? 'bg-background border border-border shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid="tab-downloads"
          >
            <Download className="inline h-3 w-3 mr-1" />Downloads
            {isPaid && queueCount > 0 && (
              <span
                className="ml-1.5 rounded-full bg-amber-500/25 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 animate-pulse"
                data-testid="downloads-queue-badge"
              >
                {queueCount}
              </span>
            )}
            {isPaid && queueCount === 0 && chapters.length > 0 && (
              <span className="ml-1.5 rounded-full bg-sky-500/20 px-1 py-0.5 text-[10px] text-sky-700">
                {chapters.length}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-12">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <p className="text-[13px] text-muted-foreground">Loading narrators…</p>
          </div>
        ) : tab === 'narrator' ? (
          <>
            {/* Narrator grid */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-1 py-1">
              {actors.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-10 text-center">
                  <Mic className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
                  <p className="text-[13.5px] text-muted-foreground">
                    No narrator personas have been set up yet.
                    Contact your publisher administrator to add narrators.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {actors.map((actor) => (
                    <ActorCard
                      key={actor.id}
                      actor={actor}
                      selected={selectedActorId === actor.id}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Bottom action bar */}
            <div className="border-t border-border pt-3 space-y-3">
              {/* Preview from manuscript */}
              {selectedActor && (
                <div className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-2.5">
                  <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium">Preview with {selectedActor.name}</p>
                    <p className="text-[12px] text-muted-foreground">
                      Generate a 30-second clip from your first chapter — free.
                    </p>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={handlePreview}
                    disabled={previewLoading}
                    className="gap-1 flex-shrink-0"
                    data-testid="narration-preview-btn"
                  >
                    {previewLoading
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</>
                      : previewAudioUrl
                        ? <><Square className="h-3.5 w-3.5" /> Stop preview</>
                        : <><Play className="h-3.5 w-3.5" /> Hear preview</>}
                  </Button>
                </div>
              )}

              {previewAudioUrl && (
                <audio
                  controls
                  src={previewAudioUrl}
                  className="w-full h-8"
                  data-testid="narration-preview-audio"
                />
              )}

              {/* Enable narration CTA */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  {isPaid ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[12px] text-emerald-700 font-medium">
                      <Check className="h-3 w-3" /> Narration enabled for this book
                    </span>
                  ) : (
                    <div>
                      <p className="text-[13px] font-medium">Enable full narration</p>
                      <p className="text-[12px] text-muted-foreground">
                        One-time fee of{' '}
                        <strong>{pricePerBook != null ? fmt(pricePerBook) : '…'}</strong>{' '}
                        per book — full MP3 files you own.
                      </p>
                    </div>
                  )}
                </div>
                {isPaid ? (
                  <Button
                    onClick={() => setTab('generate')}
                    className="gap-1 flex-shrink-0"
                    data-testid="narration-go-generate-btn"
                  >
                    <Zap className="h-4 w-4" />
                    Generate audiobook
                  </Button>
                ) : (
                  <Button
                    onClick={handleEnableNarration}
                    disabled={!selectedActorId || saving || checkoutLoading}
                    data-testid="narration-enable-btn"
                    className="gap-1 flex-shrink-0"
                  >
                    {checkoutLoading
                      ? <Loader2 className="h-4 w-4 animate-spin" />
                      : <ChevronRight className="h-4 w-4" />}
                    {checkoutLoading
                      ? 'Redirecting…'
                      : `Enable narration${pricePerBook != null ? ` — ${fmt(pricePerBook)}` : ''}`}
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : tab === 'generate' ? (
          /* Generate Audiobook tab */
          <div className="flex-1 overflow-y-auto py-1 pr-1">
            {isPaid && (
              <GenerateSection
                pid={pid}
                narration={narration}
                onRefresh={load}
                onChaptersUpdate={setChapters}
              />
            )}
          </div>
        ) : tab === 'downloads' ? (
          /* Downloads tab — always render panel; gates download actions inside */
          <div className="flex-1 overflow-y-auto py-1 pr-1">
            <DownloadManagerPanel
              pid={pid}
              chapters={chapters}
              isPaid={isPaid}
              onQueueChange={setQueueCount}
              onOpenOfflineGuide={() => setOfflineGuideOpen(true)}
              onGoToNarrator={() => setTab('narrator')}
            />
          </div>
        ) : null}
      </DialogContent>
      {/* Stage 4: Offline Setup Guide */}
      <NarrationDownloadDialog open={offlineGuideOpen} onOpenChange={setOfflineGuideOpen} />
    </Dialog>
  );
}
